import { ContinueToNextViewButton } from "@faire/deprecated-retailer-visitor-shared/components/AppSignIn/components/ContinueToNextViewButton";
import { convertErrorToLoginError } from "@faire/deprecated-retailer-visitor-shared/components/AppSignIn/components/convertErrorToLoginError";
import { EmailSentScenario } from "@faire/deprecated-retailer-visitor-shared/components/AppSignIn/components/EmailSentWaitingView";
import { ErrorMessage } from "@faire/deprecated-retailer-visitor-shared/components/AppSignIn/components/ErrorMessage";
import { logErrorSignIn } from "@faire/deprecated-retailer-visitor-shared/components/AppSignIn/components/logErrorSignIn";
import { LoginError } from "@faire/deprecated-retailer-visitor-shared/components/AppSignIn/components/LoginError";
import { LoginWrapper } from "@faire/deprecated-retailer-visitor-shared/components/AppSignIn/components/styled";
import { View } from "@faire/deprecated-retailer-visitor-shared/components/AppSignIn/components/View";
import { useSignInEventTracker } from "@faire/deprecated-retailer-visitor-shared/components/AppSignIn/useSignInEventTracker";
import { useFetchHCaptchaToken } from "@faire/deprecated-retailer-visitor-shared/hooks/hCaptcha/useFetchHCaptchaToken";
import { useHCaptchaLogin } from "@faire/deprecated-retailer-visitor-shared/hooks/hCaptcha/useHCaptchaLogin";
import { handleKeyDown } from "@faire/deprecated-retailer-visitor-shared/lib/handleKeyDown";
import { isValidEmail } from "@faire/deprecated-retailer-visitor-shared/lib/validation";
import {
  useAssignSettingMagicLinkProminenceXP,
  useSettingMagicLinkProminenceXP,
} from "@faire/deprecated-retailer-visitor-shared/settings/useSettingSignInImprovements";
import { useSSOPasswordStore } from "@faire/deprecated-retailer-visitor-shared/stores/domain/SSO/useSSOPasswordStore";
import { useSSOStore } from "@faire/deprecated-retailer-visitor-shared/stores/domain/SSO/useSSOStore";
import { Core } from "@faire/design-tokens";
import userLoginData from "@faire/web-api--source/endpoints/www/api/user/login-data/post";
import resetPasswordWithEmail from "@faire/web-api--source/endpoints/www/api/user/reset-password/post";
import resetPassword from "@faire/web-api--source/endpoints/www/api/user/reset-password/userToken/post";
import login from "@faire/web-api--source/endpoints/www/api/v2/users/login/post";
import { trackRetailerPortalLoginModalEnterEmail } from "@faire/web-api--source/events/retailerPortal/actionCategoryOther/loginModalEnterEmail";
import { trackRetailerPortalLoginModalLoggedInWithPassword } from "@faire/web-api--source/events/retailerPortal/actionCategoryOther/loginModalLoggedInWithPassword";
import { trackRetailerPortalLoginModalMagicLink } from "@faire/web-api--source/events/retailerPortal/actionCategoryOther/loginModalMagicLink";
import { trackSigninMagicCodeClick } from "@faire/web-api--source/events/signin/click/magicCode";
import { trackSigninResetPasswordClick } from "@faire/web-api--source/events/signin/click/resetPassword";
import { trackSigninSignInPasswordSubmitClick } from "@faire/web-api--source/events/signin/click/signInPasswordSubmit";
import { trackSigninTriggersMfaSubmit } from "@faire/web-api--source/events/signin/submit/triggersMfa";
import { trackSigninLoginWithPasswordSuccess } from "@faire/web-api--source/events/signin/success/loginWithPassword";
import { trackSigninResetPasswordSuccess } from "@faire/web-api--source/events/signin/success/resetPassword";
import { ILoginDataRequest } from "@faire/web-api--source/indigofair/data/ILoginDataRequest";
import { ILoginRequest } from "@faire/web-api--source/indigofair/data/ILoginRequest";
import { ILoginResponse } from "@faire/web-api--source/indigofair/data/ILoginResponse";
import { ISendResetPasswordRequest } from "@faire/web-api--source/indigofair/data/ISendResetPasswordRequest";
import { IUser } from "@faire/web-api--source/indigofair/data/IUser";
import { LoginState } from "@faire/web-api--source/indigofair/data/LoginState";
import { ISignInMagicLinkXP } from "@faire/web-api--source/indigofair/seo/ISignInMagicLinkXP";
import { emitDatadogError } from "@faire/web/common/datadog/rum";
import { getWindowOrThrow } from "@faire/web/common/globals/getWindow";
import { useStrictLocalization } from "@faire/web/common/localization/localize";
import { Button } from "@faire/web/slate/Button";
import { Eye, EyeClosed } from "@faire/web/slate/icons/foundation/Eye";
import { Input } from "@faire/web/slate/Input";
import { HorizontalLine, Row } from "@faire/web/slate/Layout";
import { Spacer } from "@faire/web/slate/spacing";
import { Typography } from "@faire/web/slate/Typography";
import { StrictLocalMsg } from "@faire/web/ui/lib/localization";
import React, { FC, useCallback, useEffect, useState } from "react";
import styled from "styled-components";

type EnterPasswordResult = {
  nextView: View;
  mfaDescription?: string;
  mfaTitle?: string;
  userToken?: string;
  password?: string;
  email?: string;
  obfuscatedEmail?: string;
  mfaOptions?: ILoginResponse.IMfaOption[];
  emailSentScenario?: EmailSentScenario;
};

interface IProps {
  signInTitle: string;
  defaultEmail: string;
  userToken?: string;
  withEmail: boolean;
  sendResetEmail?: boolean;
  onComplete: (result: EnterPasswordResult) => void;
  onLogin: (user: IUser) => void;
}

export const EnterPasswordView: FC<IProps> = ({
  signInTitle,
  defaultEmail,
  userToken,
  withEmail,
  sendResetEmail,
  onComplete,
  onLogin,
}) => {
  const { strictLocalize } = useStrictLocalization();
  const [password, setPassword] = useState("");
  const [error, setError] = useState<LoginError>();
  const [incorrectPasswordErrorShowTime, setIncorrectPasswordErrorShowTime] =
    useState(0);
  const [passwordError, setPasswordError] = useState<string | undefined>(
    undefined
  );
  const [isLoading, setIsLoading] = useState(false);
  const { fetchHCaptchaToken } = useFetchHCaptchaToken();
  const magicLinkProminenceVariant = useSettingMagicLinkProminenceXP();
  const assignSettingMagicLinkProminenceXP =
    useAssignSettingMagicLinkProminenceXP();
  const { trackFieldValue } = useSignInEventTracker("ENTER_PASSWORD");

  const { isActive } = useSSOStore();
  const { userHasPassword } = useSSOPasswordStore();

  const validatePassword = useCallback(async () => {
    if (password.length === 0) {
      setPasswordError(
        strictLocalize({
          id: "MF++dd",
          defaultMessage: "Enter a password to continue",
          description: { text: "Prompt for user to enter password" },
        })
      );
      return false;
    }
    return true;
  }, [password.length, strictLocalize]);

  const onForgotPassword = useCallback(
    async (emailAddress: string) => {
      if (!isValidEmail(emailAddress)) {
        return;
      }
      const noRetailerMessage = strictLocalize({
        id: "RdPpZr",
        defaultMessage: "No associated retailer with email address",
        description: {
          text: "Error showing the user that no retailer exists for the entered email address",
        },
      });
      if (withEmail) {
        try {
          setIsLoading(true);
          const loginData = await fetchHCaptchaToken(true).then(() =>
            userLoginData(
              ILoginDataRequest.build({ email_address: emailAddress })
            )
          );
          const newUserToken = loginData.user_token;

          if (newUserToken) {
            await resetPassword(
              newUserToken,
              ISendResetPasswordRequest.build({})
            );
            trackSigninResetPasswordSuccess(
              View.ENTER_LOGIN,
              "LOGIN_DATA_USER_TOKEN"
            );
          } else {
            await resetPasswordWithEmail(
              ILoginDataRequest.build({ email_address: emailAddress })
            );
            trackSigninResetPasswordSuccess(
              View.ENTER_LOGIN,
              "LOGIN_DATA_EMAIL_ADDRESS"
            );
          }
          onComplete({
            nextView: View.RESET_PASSWORD,
            userToken: newUserToken,
            email: emailAddress,
            obfuscatedEmail: loginData.partial_alias,
          });
        } catch (e) {
          emitDatadogError(e);
          const loginError = convertErrorToLoginError(e, strictLocalize);
          setError(loginError);
        } finally {
          setIsLoading(false);
        }
      } else if (userToken) {
        try {
          setIsLoading(true);
          await resetPassword(userToken, ISendResetPasswordRequest.build({}));
          trackSigninResetPasswordSuccess(View.ENTER_LOGIN, "USER_TOKEN");
          onComplete({ nextView: View.RESET_PASSWORD });
        } catch (e) {
          setError(convertErrorToLoginError(e, strictLocalize));
        } finally {
          setIsLoading(false);
        }
      } else if (emailAddress) {
        try {
          setIsLoading(true);
          await resetPasswordWithEmail(
            ILoginDataRequest.build({ email_address: emailAddress })
          );
          trackSigninResetPasswordSuccess(View.ENTER_LOGIN, "EMAIL_ADDRESS");
          onComplete({ nextView: View.RESET_PASSWORD });
        } catch (e) {
          setError(convertErrorToLoginError(e, strictLocalize));
        } finally {
          setIsLoading(false);
        }
      } else {
        setError(new LoginError(noRetailerMessage));
      }
    },
    [strictLocalize, withEmail, userToken, fetchHCaptchaToken, onComplete]
  );

  useEffect(() => {
    if (withEmail) {
      trackRetailerPortalLoginModalEnterEmail("PASSWORD");
      if (sendResetEmail) {
        onForgotPassword(defaultEmail);
      }
    }
  }, [withEmail, defaultEmail, sendResetEmail, onForgotPassword]);

  useEffect(() => {
    if (isActive && !userHasPassword) {
      logErrorSignIn(
        "ENTER_PASSWORD",
        "Wrong view for SSO user without password"
      );
    }
  }, [isActive, userHasPassword]);

  useEffect(() => {
    assignSettingMagicLinkProminenceXP();
  }, [assignSettingMagicLinkProminenceXP]);

  const onMagicLink = async () => {
    trackSigninMagicCodeClick(View.ENTER_PASSWORD);
    if (!isValidEmail(defaultEmail)) {
      return;
    }
    try {
      setIsLoading(true);
      await login(
        ILoginRequest.build({
          email_address: defaultEmail,
          user_token: !defaultEmail ? userToken : undefined,
        })
      );
      trackRetailerPortalLoginModalMagicLink();
      onComplete({ nextView: View.ENTER_CODE, email: defaultEmail });
    } catch (e) {
      setError(convertErrorToLoginError(e, strictLocalize));
    } finally {
      setIsLoading(false);
    }
  };

  const onContinue = async () => {
    const isValidPassword = await validatePassword();
    const isEmailValid = isValidEmail(defaultEmail);

    if (!isEmailValid || !isValidPassword) {
      trackSigninSignInPasswordSubmitClick("INCORRECT", "EMPTY_PASSWORD");
      return;
    }
    try {
      const loginRequest = ILoginRequest.build({
        password,
        email_address: defaultEmail,
        user_token: !defaultEmail ? userToken : undefined,
        enable_mfa_method_selection: true,
      });

      setIsLoading(true);
      const { mfa_description, mfa_title, user, mfa_options, login_state } =
        await hCaptchaLogin(loginRequest);
      trackSigninLoginWithPasswordSuccess(View.ENTER_LOGIN, login_state);
      if (login_state === LoginState.ISSUED_AUTOMATIC_LOGIN_CODE) {
        onComplete({
          nextView: View.AUTO_SIGN_IN_LINK_SENT,
          password,
          email: defaultEmail,
          userToken,
          emailSentScenario: EmailSentScenario.AUTO,
        });
        return;
      }

      if (login_state === LoginState.MFA_METHOD_SELECTION_REQUIRED) {
        trackSigninSignInPasswordSubmitClick("MFA_TRIGGERED", login_state);
        trackSigninTriggersMfaSubmit("ENTER_PASSWORD");
        onComplete({
          nextView: View.SELECT_MFA_OPTION,
          password,
          userToken,
          mfaOptions: mfa_options,
          mfaTitle: mfa_title,
          mfaDescription: mfa_description,
        });
        return;
      }
      if (login_state === LoginState.MFA_REQUIRED) {
        trackSigninSignInPasswordSubmitClick("MFA_TRIGGERED", login_state);
        trackSigninTriggersMfaSubmit("ENTER_PASSWORD");
        onComplete({
          nextView: View.ENTER_MFA,
          password,
          mfaDescription: mfa_description,
          mfaTitle: mfa_title,
        });
        return;
      }
      if (!user) {
        trackSigninSignInPasswordSubmitClick("INCORRECT", "INVALID_USER");
        setError(
          new LoginError(
            strictLocalize({
              id: "bw8Bnw",
              defaultMessage: "Invalid credentials",
              description: {
                text: "Email or password that was entered is incorrect",
              },
            })
          )
        );
        return;
      }
      if (
        "credentials" in getWindowOrThrow().navigator &&
        "PasswordCredential" in getWindowOrThrow()
      ) {
        const cred = new PasswordCredential({
          id: defaultEmail,
          password: password,
        });
        getWindowOrThrow()
          .navigator.credentials.store(cred)
          .catch(() => {
            // Ignore.
          });
      }
      trackRetailerPortalLoginModalLoggedInWithPassword();
      onLogin(user);
      trackSigninSignInPasswordSubmitClick("CORRECT", "NO_MFA");
      onComplete({
        nextView: View.LOADING,
      });
    } catch (e) {
      const loginError = convertErrorToLoginError(e, strictLocalize);
      logErrorSignIn("ENTER_PASSWORD", loginError.message);
      const isIncorrectPasswordError = loginError.httpStatusCode === 400;
      if (isIncorrectPasswordError) {
        trackSigninSignInPasswordSubmitClick("INCORRECT", "INVALID_PASSWORD");
      }
      if (loginError.httpStatusCode === 429) {
        setError(
          new LoginError(
            strictLocalize({
              id: "wzldCA",
              defaultMessage:
                "Exceeded maximum allowed number of login attempts",
              description: {
                text: "Error that shows when a user has attempted to log in too many times",
              },
            })
          )
        );
      } else if (
        isIncorrectPasswordError &&
        incorrectPasswordErrorShowTime > 0
      ) {
        setPasswordError(
          strictLocalize({
            id: "PHTcwX",
            defaultMessage: "Incorrect password",
            description: {
              text: "This error shows the password is not correct",
            },
          })
        );
      } else if (isIncorrectPasswordError) {
        setIncorrectPasswordErrorShowTime((prevState) => prevState + 1);
        setPasswordError(
          strictLocalize({
            id: "NxodaO",
            defaultMessage: "Incorrect password, please try again",
            description: {
              text: "This error shows the password is not correct and advice user to try again",
            },
          })
        );
      } else {
        setError(loginError);
      }
    } finally {
      setIsLoading(false);
    }
  };

  const { hCaptchaLogin } = useHCaptchaLogin();
  const handleChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    setPasswordError(undefined);
    setError(undefined);
    setPassword(event.currentTarget.value);
  };
  const [showPassword, setShowPassword] = useState(false);
  return (
    <LoginWrapper>
      <Typography variant="displaySSerifRegular">{signInTitle}</Typography>
      {withEmail ? (
        <>
          <Spacer height="1x" />
          <Typography>{defaultEmail}</Typography>
          <Spacer height="3x" />
        </>
      ) : null}
      <Input
        label={strictLocalize({
          id: "7teRwl",
          defaultMessage: "Password",
          description: {
            text: "Title for user's email address",
            img: "https://cdn.faire.com/fastly/9b74aae6cbf3213e23a54a9d0741c9cb9022c95658c97d9df588161c11b15a9c.png",
          },
        })}
        autoFocus
        autoComplete="password"
        role="password"
        aria-label={strictLocalize({
          id: "WSZNMU",
          defaultMessage: "Password",
          description: { text: "Accessible name for user's password" },
        })}
        name="password"
        type={showPassword ? "text" : "password"}
        suffixNode={
          <button
            type="button"
            data-test-id="eye-button"
            onClick={() => {
              setShowPassword(!showPassword);
            }}
          >
            {showPassword ? (
              <Eye color={Core.text.subdued} fontSize="24px" />
            ) : (
              <EyeClosed color={Core.text.subdued} fontSize="24px" />
            )}
          </button>
        }
        data-test-id="password"
        value={password}
        onChange={handleChange}
        onBlur={() => {
          trackFieldValue("userpassword", "");
        }}
        onKeyDown={handleKeyDown(onContinue)}
        error={passwordError}
      />
      {passwordError ? <Spacer height="1x" /> : <Spacer height="3x" />}
      {magicLinkProminenceVariant !== ISignInMagicLinkXP.Bucket.VARIANT_B ? (
        <>
          <Button
            style={{ alignSelf: "flex-start" }}
            variant="tertiary"
            onClick={() => {
              onForgotPassword(defaultEmail);
              trackSigninResetPasswordClick(View.ENTER_PASSWORD);
            }}
            data-test-id="reset-pswd-link"
          >
            <StrictLocalMsg
              id="fTiJ2a"
              defaultMessage="Forgot password?"
              description={{
                text: "Link for reset password",
                img: "https://cdn.faire.com/fastly/9b74aae6cbf3213e23a54a9d0741c9cb9022c95658c97d9df588161c11b15a9c.png",
              }}
            />
          </Button>
          <Spacer height="3x" />
        </>
      ) : null}
      <ContinueToNextViewButton
        onClick={onContinue}
        isLoading={isLoading}
        TextOverride={
          <StrictLocalMsg
            id="MfO/qT"
            defaultMessage="Sign in"
            description={{ text: "Submit password and sign in" }}
          />
        }
      />
      <ErrorMessage error={error} align="left" small />
      {error ? null : <Spacer height="3x" />}
      <Row align="center">
        <HorizontalLine />
        <Spacer width="2x" />
        <Typography>
          <StrictLocalMsg
            id="XBzqWF"
            defaultMessage="or"
            description={{ text: "Use 'this' or 'that'" }}
          />
        </Typography>
        <Spacer width="2x" />
        <HorizontalLine />
      </Row>
      <Spacer height="3x" />
      <SendMagicLinkButton
        onClick={onMagicLink}
        data-test-id="send-magic-link-bttn"
        disabled={isLoading}
      >
        {magicLinkProminenceVariant === ISignInMagicLinkXP.Bucket.CONTROL ? (
          <StrictLocalMsg
            id="qVJHC3"
            defaultMessage="Email me a link to sign in"
            description={{
              text: "An option for the user to send an email with a link to authenticate instead of entering password",
              img: "https://cdn.faire.com/fastly/9b74aae6cbf3213e23a54a9d0741c9cb9022c95658c97d9df588161c11b15a9c.png",
            }}
          />
        ) : (
          <StrictLocalMsg
            id="WAROQa"
            defaultMessage="Email me a code"
            description={{
              text: "An option for the user to send an email with a link to authenticate instead of entering password",
              img: "https://cdn.faire.com/fastly/2efbd879060457573534c5cd8cc91c76c0b8632a90b9e875ffa627fc555203b7.png",
            }}
          />
        )}
      </SendMagicLinkButton>
    </LoginWrapper>
  );
};

// This is a temporary quick fix for a translation issue.
// This styled should be removed once french fits in a single line
// or once our button handles multi line content
const SendMagicLinkButton = styled(Button).attrs({ variant: "secondary" })`
  line-height: 20px;
`;
